import { Navigate, Route, useLocation } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import './styles.css';
import {
  AlertDisplay,
  Header,
  OAuthRequestDialog,
  Progress,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { DevServicesChartSummaryPage } from '@internal/plugin-dev-services-chart-summary';
import { testJwtLookupPermission } from '@internal/plugin-test-jwt-common';
import { TestJwtLookupPage } from './components/TestJwt';
import { LdapAuthFrontendPage } from '@immobiliarelabs/backstage-plugin-ldap-auth';
import { RbacPage } from '@backstage-community/plugin-rbac';
import { ProductionNamespacesFieldExtension } from './scaffolder/ProductionNamespacesField/extensions';
import { ValidateApplicationNameFieldExtension } from './scaffolder/ValidateApplicationName/extensions';
import { useApi, discoveryApiRef } from '@backstage/core-plugin-api';
import { useEffect } from 'react';
import { setFaroView } from './services/faro.service';
import { Container } from '@material-ui/core';
import HelpInstructions from './components/HelpInstructions';
import LogoFull from './components/Root/LogoFull';
import { useSignInStyles, useLdapRefresh } from './helpers';
import { healthStatusCatalogTableColumnsFunc } from './catalogTableColumns';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import { brightlyLightTheme } from './theme/brightlyLightTheme';
import LightIcon from '@material-ui/icons/WbSunny';
import DarkIcon from '@material-ui/icons/Brightness2';
import { brightlyDarkTheme } from './theme/brightlyDarkTheme';
import { UnifiedThemeProvider } from '@backstage/theme';

function FaroRouteTracker() {
  const location = useLocation();
  useEffect(() => {
    setFaroView(location.pathname);
  }, [location.pathname]);
  return null;
}

let locationRef = globalThis.window.location;

export const setLocationRef = (ref: Location) => {
  locationRef = ref;
};

const app = createApp({
  apis,
  themes: [
    {
      id: 'brightly-light',
      title: 'Brightly (light)',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={brightlyLightTheme} children={children} />
      ),
    },
    {
      id: 'brightly-dark',
      title: 'Brightly (dark)',
      variant: 'dark',
      icon: <DarkIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={brightlyDarkTheme} children={children} />
      ),
    },
  ],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => {
      const discoveryApi = useApi(discoveryApiRef);
      const classes = useSignInStyles();

      const { status, showHelp, handleSignInError } = useLdapRefresh(
        discoveryApi,
        locationRef,
      );
      if (status === 'loading') {
        // eslint-disable-next-line no-console
        console.log('[LDAP Refresh] Waiting for backend response...');
        return (
          <div className={classes.progressWrapper}>
            <Progress />
            <div className={classes.connectingText}>
              Connecting to backend...
            </div>
          </div>
        );
      }

      return (
        <div className={classes.overrideMuiMain}>
          <Header title={<LogoFull />} pageTitleOverride="Sign in" />
          <Container className={classes.mainContainer}>
            {showHelp ? (
              <HelpInstructions />
            ) : (
              <LdapAuthFrontendPage
                {...props}
                provider="ldap"
                onSignInError={handleSignInError}
                options={{
                  helperTextUsername:
                    'Use the first.last format from your account (first.last@si-eam.com).',
                }}
              />
            )}
          </Container>
        </div>
      );
    },
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="catalog" />} />
    <Route
      path="/catalog"
      element={
        <CatalogIndexPage
          columns={healthStatusCatalogTableColumnsFunc}
          tableOptions={{ thirdSortClick: false }}
        />
      }
    />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
        <Mermaid config={{ themeVariables: { lineColor: 'currentColor' } }} />
      </TechDocsAddons>
    </Route>
    <Route
      path="/create"
      element={
        <ScaffolderPage
          groups={[
            {
              title: 'Recommended',
              filter: entity =>
                entity?.metadata?.tags?.includes('recommended') ?? false,
            },
            {
              title: 'Updates',
              filter: entity =>
                entity?.metadata?.tags?.includes('updates') ?? false,
            },
          ]}
        />
      }
    >
      <ScaffolderFieldExtensions>
        <ValidateApplicationNameFieldExtension />
        <ProductionNamespacesFieldExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/chart-summary" element={<DevServicesChartSummaryPage />} />
    <Route
      path="/test-jwt/lookup"
      element={
        <RequirePermission permission={testJwtLookupPermission}>
          <TestJwtLookupPage />
        </RequirePermission>
      }
    />
    <Route path="/rbac" element={<RbacPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <FaroRouteTracker />
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
